import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, ArrowLeft, TrendingUp, Users, MapPin, MessageCircle, Brain } from "lucide-react";
import HavnsLogo from "../components/HavnsLogo";
import Footer from "../components/Footer";

const Insights = () => {
  const navigate = useNavigate();

  const researchAreas = [
    {
      number: "01",
      title: "Platonic Chemistry",
      description: "What makes two people click as friends? We study conversation patterns, shared interests, and communication styles to improve our matching.",
    },
    {
      number: "02",
      title: "Travel Compatibility",
      description: "Solo travel with a companion requires alignment. We research travel styles, pace preferences, and budget expectations to create better journey matches.",
    },
    {
      number: "03",
      title: "Local Connection",
      description: "How do strangers become friends? We analyze successful meetups to understand what environments and activities foster genuine connection.",
    },
    {
      number: "04",
      title: "Community Health",
      description: "We continuously monitor for romantic advances, ensuring our platonic space remains protected. Our detection systems improve with every report.",
    },
    {
      number: "05",
      title: "Member Wellbeing",
      description: "Connection should energize, not exhaust. We study engagement patterns to ensure Havns remains a positive space for our members.",
    },
  ];

  const findings = [
    {
      stat: "73%",
      insight: "of successful travel companions had at least 3 shared interests beyond travel itself.",
    },
    {
      stat: "2.5x",
      insight: "more likely to meet again after a coffee meetup vs. a group event of 10+ people.",
    },
    {
      stat: "89%",
      insight: "of members say Havns helped them find meaningful platonic connections.",
    },
  ];

  return (
    <div className="min-h-screen bg-[#FBF9F7]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FBF9F7]/90 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between h-20">
          <HavnsLogo className="text-2xl" linkTo="/" />
          
          {/* Navigation Links */}
          <div className="hidden md:flex items-center gap-8">
            <Link 
              to="/philosophy" 
              className="text-[#666] hover:text-[#1A1A1A] text-sm font-medium tracking-wide transition-colors"
            >
              Philosophy
            </Link>
            <Link 
              to="/movement" 
              className="text-[#666] hover:text-[#1A1A1A] text-sm font-medium tracking-wide transition-colors"
            >
              Movement
            </Link>
            <Link 
              to="/insights" 
              className="text-[#666] hover:text-[#1A1A1A] text-sm font-medium tracking-wide transition-colors"
            >
              Insights
            </Link>
          </div>

          <Button
            onClick={() => navigate("/")}
            className="bg-[#1A1A1A] text-white hover:bg-[#333] rounded-full px-8 py-6 text-sm font-medium"
          >
            Join Havns
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-[#666] hover:text-[#1A1A1A] mb-12 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#C5B358] font-medium tracking-widest text-sm uppercase mb-6">
                Community Insights
              </p>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#1A1A1A] leading-[1.1] mb-8">
                The science of<br />
                <span className="italic">platonic connection.</span>
              </h1>
            </div>
            <div className="aspect-[4/3] rounded-[2rem] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&fit=crop"
                alt="Research and collaboration"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#C5B358] font-medium tracking-widest text-sm uppercase mb-6">
                The Mission
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] leading-tight mb-8">
                Understanding what makes platonic bonds work.
              </h2>
              <p className="text-xl text-[#666] leading-relaxed">
                Dating apps have spent billions studying romantic chemistry. We're pioneering 
                the science of platonic connection—understanding what makes two people become 
                friends, travel companions, or trusted confidants.
              </p>
            </div>
            <div className="aspect-square rounded-[2rem] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1517048676732-d65bc937f952?w=600&fit=crop"
                alt="Team collaboration"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Research Areas */}
      <section className="py-24 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-16">
            <p className="text-[#C5B358] font-medium tracking-widest text-sm uppercase mb-6">
              Research Focus
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl text-white leading-tight">
              What we study.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {researchAreas.map((area, idx) => (
              <div key={idx} className="border-t border-white/20 pt-8">
                <span className="text-[#C5B358] font-mono text-sm">{area.number}</span>
                <h3 className="font-serif text-xl text-white mt-4 mb-3">
                  {area.title}
                </h3>
                <p className="text-white/70 leading-relaxed">
                  {area.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Findings */}
      <section className="py-24 bg-[#FBF9F7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-[#C5B358] font-medium tracking-widest text-sm uppercase mb-6">
              Key Findings
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A]">
              What we've learned.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {findings.map((finding, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-8 text-center">
                <p className="font-serif text-6xl text-[#C5B358] mb-4">{finding.stat}</p>
                <p className="text-[#666] leading-relaxed">{finding.insight}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Methods */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="aspect-square rounded-[2rem] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=600&fit=crop"
                alt="Research methods"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="text-[#C5B358] font-medium tracking-widest text-sm uppercase mb-6">
                Research Methods
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] leading-tight mb-8">
                How we learn.
              </h2>
              <div className="space-y-6">
                {[
                  { icon: Brain, label: "Behavioral Analysis", desc: "Studying interaction patterns across journeys and meetups" },
                  { icon: MessageCircle, label: "Member Surveys", desc: "Regular feedback from our community on what works" },
                  { icon: TrendingUp, label: "Outcome Tracking", desc: "Measuring friendship formation and travel satisfaction" },
                  { icon: Users, label: "Focus Groups", desc: "Deep conversations with members about their experiences" },
                ].map((method, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#FBF9F7] flex items-center justify-center flex-shrink-0">
                      <method.icon className="w-5 h-5 text-[#C5B358]" />
                    </div>
                    <div>
                      <h3 className="font-medium text-[#1A1A1A] mb-1">{method.label}</h3>
                      <p className="text-[#666]">{method.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mb-8">
            Help us learn more.
          </h2>
          <p className="text-xl text-[#666] max-w-2xl mx-auto mb-12">
            Every member contributes to our understanding of platonic connection. 
            Join us and be part of pioneering research.
          </p>
          <Button
            onClick={() => navigate("/")}
            className="bg-[#1A1A1A] text-white hover:bg-[#333] rounded-full px-12 py-7 text-base font-medium inline-flex items-center gap-3"
          >
            Join Havns
            <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>

      <Footer bgColor="bg-[#FBF9F7]" />
    </div>
  );
};

export default Insights;
